import { parse } from 'parse5';
// Use namespaced types to more easily differentiate between similar types (ie, ChildNode from parse5 and Node from slate).
import type * as parse5 from 'parse5';
import type * as slate from 'slate';

import type { RichTextViewerProps } from '../RichTextViewer.types';

import parseTextToCustomSlateNodes from './interactionRenderer';

const checkElement = (node: parse5.ChildNode | undefined): node is parse5.Element =>
  !!node && node.nodeName !== '#text' && node.nodeName !== '#comment';
const checkTextNode = (node: parse5.ChildNode | undefined): node is parse5.TextNode =>
  !!node && node.nodeName === '#text';
const checkComment = (node: parse5.ChildNode | undefined): node is parse5.TextNode =>
  !!node && node.nodeName === '#comment';

// Mapping of HTML tags to slate types. b, del, i, and a tags are labeled as skip because they need special logic
export const htmlToSlateMap = {
  h1: 'heading-1',
  h2: 'heading-2',
  h3: 'heading-3',
  h4: 'heading-4',
  ul: 'unordered-list',
  ol: 'ordered-list',
  li: 'list-item',
  table: 'table',
  tr: 'table-row',
  td: 'table-cell',
  th: 'table-cell',
  tbody: 'table-body',
  thead: 'table-head',
  p: 'paragraph',
  img: 'image',
  b: 'skip',
  del: 'skip',
  i: 'skip',
  a: 'skip'
} as const;

// Inline HTML tags to slate types
export const inlineHtmlToStyleMap = {
  b: 'bold',
  del: 'line-through',
  i: 'italic',
  a: 'href'
} as const;

// Maps HTML Attributes to an object. Right now this is specifically used for list items.

interface InlineParentNodes extends slate.PegaCustom.CustomElement {
  href?: string;
  italic?: boolean;
  bold?: boolean;
  text?: string;
}

const getAttributes = (node: parse5.ChildNode) => {
  if (checkElement(node) && node.attrs.length) {
    switch (node.nodeName) {
      case 'li':
        return Object.fromEntries(
          node.attrs.flatMap(attr => {
            // eslint-disable-next-line sonarjs/no-nested-switch
            switch (attr.name) {
              case 'data-id':
                return [['id', attr.value]];
              case 'data-parent-id':
                return [['parentId', attr.value]];
              case 'data-level':
                return [['level', attr.value]];
              case 'data-order':
                return [['order', attr.value]];
              default:
                return [];
            }
          })
        );
      case 'img': {
        const imgAttrs = ['src', 'alt'];
        const attrsObj: { [key: string]: string } = {};
        for (let i = 0; i < node.attrs.length; i += 1) {
          const { name, value } = node.attrs[i];
          if (imgAttrs.includes(name)) {
            attrsObj[name] = value;
          }
        }
        return attrsObj;
      }
      case 'td':
      case 'th': {
        const alignAttr = node.attrs.find(attr => attr.name === 'align');
        const alignment = alignAttr?.value.toLowerCase();
        if (alignment === 'left' || alignment === 'center' || alignment === 'right') {
          return { align: alignment };
        }
        return {};
      }
      default:
        return {};
    }
  }
  return {};
};

const getSlateNode = (
  node: parse5.ChildNode,
  parentNode?: slate.Element | slate.Text,
  interactionRenderers?: RichTextViewerProps['interactionRenderers']
): { node: slate.Element | slate.Text; collapseNext?: boolean } => {
  if (checkTextNode(node)) {
    if (node.parentNode.nodeName === 'body') {
      return {
        node: {
          type: 'paragraph',
          children: [{ text: node.value }]
        }
      };
    }
    // Make sure to spread any parent style attributes for slates text nodes
    const children = parseTextToCustomSlateNodes(
      node.value,
      interactionRenderers
    ) as slate.PegaCustom.CustomElement[];
    if (children.length === 1) {
      const tempParentNode = parentNode as InlineParentNodes;
      if (children[0]?.type === 'custom') {
        delete tempParentNode?.text;
      }

      return { node: { ...tempParentNode, ...children[0] } as slate.Text };
    }
    return {
      node: {
        ...parentNode,
        children
      } as slate.Element
    };
  }

  const type = htmlToSlateMap[node.nodeName as keyof typeof htmlToSlateMap];
  // If the type of the HTML tag is an inline (b, i, del, a) we want to let the recursive function know
  // that it needs to collapse the next node, also we are setting the correct style mark to true
  if (type === 'skip') {
    const style = inlineHtmlToStyleMap[node.nodeName as 'b' | 'i' | 'del' | 'a'];
    let value: string | boolean = true;
    if (node.nodeName === 'a') {
      const hrefObj = node.attrs?.find(attr => attr.name === 'href');
      value = hrefObj ? hrefObj.value : value;
    }
    return {
      node: { ...parentNode, [style]: value, text: '' } as slate.Text,
      collapseNext: true
    };
  }

  const attributes = getAttributes(node);
  const nodeObj = {
    type: type || 'paragraph',
    ...attributes,
    ...(node.nodeName === 'th' && { isHeaderCell: true }),
    children: [{ text: '' }]
  } as slate.Element;
  return { node: nodeObj };
};

// Recursive function that will convert Parse5Nodes into Slate compatible nodes
const convertNodes = (
  nodes: parse5.ChildNode[],
  parentNode?: slate.Element | slate.Text,
  interactionRenderers?: RichTextViewerProps['interactionRenderers']
): (slate.Element | slate.Text)[] => {
  const returnNodes: (slate.Element | slate.Text)[] = [];
  nodes.forEach(node => {
    const { node: slateNode, collapseNext } = getSlateNode(node, parentNode, interactionRenderers);
    if (!checkComment(node)) {
      let returnNode = slateNode;
      if (checkElement(node) && node.childNodes && node.childNodes.length) {
        if (collapseNext) {
          returnNode = convertNodes(node.childNodes, slateNode, interactionRenderers)[0];
        } else {
          (slateNode as slate.Element).children = convertNodes(
            node.childNodes,
            undefined,
            interactionRenderers
          );
          returnNode = slateNode;
        }
      }

      returnNodes.push(returnNode);
    }
  });
  return returnNodes;
};

const defaultValue = [{ type: 'paragraph', children: [{ text: '' }] }] satisfies slate.Element[];

export const convertHtml = (
  html: string,
  interactionRenderers?: RichTextViewerProps['interactionRenderers']
): (slate.Element | slate.Text)[] => {
  // Wrap the html in another body in case the value given has no body
  const doc = parse(`<body>${html.replace('\n', '')}</body>`);
  let nodesToConvert = doc.childNodes;
  // If we have a root tag
  if (doc.childNodes.length === 1) {
    const root = doc.childNodes[0];
    if (!checkElement(root)) return defaultValue;

    const body = root.childNodes[1];
    if (!checkElement(body)) return defaultValue;

    nodesToConvert = body.childNodes;
  }
  return convertNodes(nodesToConvert, undefined, interactionRenderers);
};
